import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  clampStepCount,
  clampWaitDays,
  isWarmupOnly,
  landingPath,
  COLLATERAL_ID,
  SEQUENCE_BOUNDS,
  type Campaign,
  type CampaignCollateral,
  type LandingImageSelection,
  type Mailbox,
  type SequenceStep,
  type SequenceStepInput,
} from '@revbase/shared';
import {
  Send,
  Sparkles,
  Wand2,
  Save,
  Trash2,
  Plus,
  Clock,
  Mail,
  ArrowLeft,
  AlertCircle,
  Globe,
  Image as ImageIcon,
  Copy,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { db } from '../lib/firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';
import {
  Card,
  Button,
  Input,
  Select,
  Textarea,
  Field,
  PageHeader,
  CardTitle,
  SectionCard,
  Skeleton,
} from '../components/ui.js';

type EditableStep =
  | { type: 'email'; subjectTemplate: string; bodyTemplate: string; aiGenerated: boolean }
  | { type: 'wait'; waitDays: number };

interface MailboxRow extends Mailbox {
  id: string;
}

export function CampaignBuilder() {
  const { tenantId } = useAuth();
  const navigate = useNavigate();
  // Present in the /campaigns/:id/edit route, absent in /campaigns/new.
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  const [selectedMailboxes, setSelectedMailboxes] = useState<string[]>([]);
  const [steps, setSteps] = useState<EditableStep[]>([
    { type: 'email', subjectTemplate: '', bodyTemplate: '', aiGenerated: false },
  ]);

  // Send window + throttle: preserved across an edit so we never silently reset
  // a campaign's schedule. Defaults match the create path.
  const [sendWindow, setSendWindow] = useState<Campaign['sendWindow']>({
    start: '09:00',
    end: '17:00',
    days: [1, 2, 3, 4, 5],
    tz: 'America/New_York',
  });
  const [throttle, setThrottle] = useState<Campaign['throttle']>({
    perMailboxPerDay: 50,
    minGapMinutes: 5,
  });

  // AI drafting controls. offer + icp are required; the rest sharpen the copy.
  const [offer, setOffer] = useState('');
  const [icp, setIcp] = useState('');
  const [tone, setTone] = useState('direct, friendly');
  const [senderName, setSenderName] = useState('');
  const [senderCompany, setSenderCompany] = useState('');
  const [valueProps, setValueProps] = useState('');
  const [cta, setCta] = useState('');
  const [painPoints, setPainPoints] = useState('');
  // Sequence shape: how many emails to draft and how long to wait between them.
  const [stepCount, setStepCount] = useState<number>(
    SEQUENCE_BOUNDS.stepCount.default,
  );
  const [waitDays, setWaitDays] = useState<number>(
    SEQUENCE_BOUNDS.waitDays.default,
  );
  const [drafting, setDrafting] = useState(false);
  // Per-step "Improve with AI": the index currently rewriting, and the
  // per-step free-text instruction keyed by step index.
  const [rewritingIdx, setRewritingIdx] = useState<number | null>(null);
  const [rewriteInstructions, setRewriteInstructions] = useState<
    Record<number, string>
  >({});

  // Whether the sequence already has AI-drafted email steps, so the draft
  // button can read "Regenerate" instead of "Draft with AI".
  const hasDraftedSteps = steps.some(
    (s) => s.type === 'email' && s.aiGenerated,
  );

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Loading / not-found state while fetching an existing campaign for edit.
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Campaign collateral: product URL in, live scrape/extract status, curate
  // images, publish a landing page, and wire the {{landingUrl}} link.
  const [productUrl, setProductUrl] = useState('');
  const [collateral, setCollateral] = useState<CampaignCollateral | null>(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [imgSelected, setImgSelected] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  // Live collateral doc for this campaign (no polling): drives the status +
  // preview. Only meaningful once the campaign is saved (has an id).
  useEffect(() => {
    if (!tenantId || !id) {
      setCollateral(null);
      return;
    }
    return onSnapshot(
      doc(db, `tenants/${tenantId}/campaigns/${id}/collateral/${COLLATERAL_ID}`),
      (snap) => {
        const c = snap.exists() ? (snap.data() as CampaignCollateral) : null;
        setCollateral(c);
        if (c?.sourceUrl) setProductUrl((p) => p || c.sourceUrl);
        if (c && (c.status === 'ready' || c.status === 'published')) {
          // Default-select every harvested image the first time collateral is ready.
          setImgSelected((prev) =>
            Object.keys(prev).length
              ? prev
              : Object.fromEntries(c.images.map((i) => [i.imageId, true])),
          );
        }
      },
    );
  }, [tenantId, id]);

  const collateralBusy =
    generating ||
    collateral?.status === 'scraping' ||
    collateral?.status === 'extracting';

  const landingUrl = collateral?.landingSlug
    ? `${window.location.origin}${landingPath(collateral.landingSlug)}`
    : '';

  const generateCollateral = async () => {
    if (!id) return;
    if (!productUrl.trim()) {
      setError('Enter your product website URL.');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      await api.generateCollateral({ campaignId: id, url: productUrl.trim() });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Collateral generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const regenerateCollateral = async () => {
    if (!id) return;
    setGenerating(true);
    setError(null);
    try {
      // force a fresh scrape: "Try again" must bypass the cached (possibly
      // failed) site scrape, not replay it.
      await api.regenerateCollateral({
        campaignId: id,
        collateralId: COLLATERAL_ID,
        force: true,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Regeneration failed');
    } finally {
      setGenerating(false);
    }
  };

  const publishLanding = async () => {
    if (!id || !collateral) return;
    setPublishing(true);
    setError(null);
    try {
      const selections: LandingImageSelection[] = collateral.images.map((i) => ({
        imageId: i.imageId,
        use: imgSelected[i.imageId] ?? false,
      }));
      await api.publishLandingPage({
        campaignId: id,
        collateralId: COLLATERAL_ID,
        selections,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // Append {{landingUrl}} to every email step body that doesn't already use it.
  const insertLandingLink = () => {
    setSteps((prev) =>
      prev.map((s) =>
        s.type === 'email' && !s.bodyTemplate.includes('{{landingUrl}}')
          ? {
              ...s,
              bodyTemplate: `${s.bodyTemplate.trimEnd()}\n\nSee more: {{landingUrl}}`,
            }
          : s,
      ),
    );
  };

  const copyLandingUrl = () => {
    if (!landingUrl) return;
    void navigator.clipboard.writeText(landingUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      const snap = await getDocs(
        query(
          collection(db, `tenants/${tenantId}/mailboxes`),
          where('status', '==', 'active'),
        ),
      );
      // Warmup-only mailboxes are never selectable as campaign senders; the
      // server rejects them too, this just keeps them out of the picker.
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Mailbox) }))
        .filter((m) => !isWarmupOnly(m));
      setMailboxes(rows);
      // Only auto-select a default mailbox when creating; edit mode prefills
      // the campaign's saved mailboxes below.
      if (!isEdit && rows[0]) setSelectedMailboxes([rows[0].id]);
    })();
  }, [tenantId, isEdit]);

  // Edit mode: load the campaign doc + its ordered steps and prefill the form.
  useEffect(() => {
    if (!tenantId || !id) return;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const campaignSnap = await getDoc(
          doc(db, `tenants/${tenantId}/campaigns/${id}`),
        );
        if (!campaignSnap.exists()) {
          setLoadError('Campaign not found.');
          return;
        }
        const c = campaignSnap.data() as Campaign;
        setName(c.name);
        setSelectedMailboxes(c.mailboxIds);
        setSendWindow(c.sendWindow);
        setThrottle(c.throttle);

        const stepsSnap = await getDocs(
          query(
            collection(db, `tenants/${tenantId}/campaigns/${id}/steps`),
            orderBy('order', 'asc'),
          ),
        );
        const loaded: EditableStep[] = stepsSnap.docs.map((d) => {
          const s = d.data() as SequenceStep;
          return s.type === 'email'
            ? {
                type: 'email',
                subjectTemplate: s.subjectTemplate ?? '',
                bodyTemplate: s.bodyTemplate ?? '',
                aiGenerated: s.aiGenerated ?? false,
              }
            : { type: 'wait', waitDays: s.waitDays ?? 1 };
        });
        if (loaded.length > 0) setSteps(loaded);
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'Failed to load campaign');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, id]);

  const draft = async () => {
    if (!offer || !icp) {
      setError('Offer and ICP are required to draft.');
      return;
    }
    setDrafting(true);
    setError(null);
    try {
      // Clicking again regenerates the whole sequence from the current inputs.
      const res = await api.draftSequence({
        offer,
        icp,
        tone,
        stepCount: clampStepCount(stepCount),
        senderName,
        senderCompany,
        valueProps,
        cta,
        painPoints,
      });
      // Interleave email steps with the user-chosen wait between them.
      const gap = clampWaitDays(waitDays);
      const drafted: EditableStep[] = [];
      res.steps.forEach((s, i) => {
        drafted.push({
          type: 'email',
          subjectTemplate: s.subject,
          bodyTemplate: s.body,
          aiGenerated: true,
        });
        if (i < res.steps.length - 1) {
          drafted.push({ type: 'wait', waitDays: gap });
        }
      });
      setSteps(drafted);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  // Rewrite a single email step with AI using its current subject/body, the
  // per-step instruction, and the offer/ICP/tone context. Replaces that step's
  // subject and body in place. Surfaces errors inline rather than crashing
  // (the n8n rewriteStep task may not exist yet).
  const rewrite = async (idx: number) => {
    const step = steps[idx];
    if (!step || step.type !== 'email') return;
    if (!offer || !icp) {
      setError('Offer and ICP are required to improve a step.');
      return;
    }
    setRewritingIdx(idx);
    setError(null);
    try {
      const res = await api.rewriteStep({
        subject: step.subjectTemplate,
        body: step.bodyTemplate,
        instruction: rewriteInstructions[idx] ?? '',
        tone,
        offer,
        icp,
      });
      updateStep(idx, {
        subjectTemplate: res.subject,
        bodyTemplate: res.body,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Improve failed');
    } finally {
      setRewritingIdx(null);
    }
  };

  const updateStep = (idx: number, patch: Partial<EditableStep>) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === idx ? ({ ...s, ...patch } as EditableStep) : s)),
    );
  };

  const addEmail = () =>
    setSteps((prev) => [
      ...prev,
      { type: 'email', subjectTemplate: '', bodyTemplate: '', aiGenerated: false },
    ]);
  const addWait = () =>
    setSteps((prev) => [...prev, { type: 'wait', waitDays: 2 }]);
  const removeStep = (idx: number) =>
    setSteps((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setError(null);
    if (!name.trim()) return setError('Name your campaign.');
    if (selectedMailboxes.length === 0) return setError('Select a mailbox.');
    setSaving(true);
    try {
      const payload: SequenceStepInput[] = steps.map((s) =>
        s.type === 'email'
          ? {
              type: 'email',
              subjectTemplate: s.subjectTemplate,
              bodyTemplate: s.bodyTemplate,
              aiGenerated: s.aiGenerated,
            }
          : { type: 'wait', waitDays: s.waitDays },
      );
      if (isEdit && id) {
        // Save edits in place; don't force re-enrollment, just return to the
        // dashboard where the updated campaign is listed.
        await api.updateCampaign({
          campaignId: id,
          name,
          mailboxIds: selectedMailboxes,
          sendWindow,
          throttle,
          steps: payload,
        });
        navigate('/');
      } else {
        const { campaignId } = await api.createCampaign({
          name,
          mailboxIds: selectedMailboxes,
          sendWindow,
          throttle,
          steps: payload,
        });
        navigate(`/contacts?campaign=${campaignId}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    if (
      !window.confirm(
        'Delete this campaign? Its steps and enrollments are removed and the campaign stops sending. This cannot be undone.',
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await api.deleteCampaign({ campaignId: id });
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-8 w-56" />
        </div>
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <div
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
          {loadError}
        </div>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/')}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Send}
        eyebrow="Sending"
        title={isEdit ? 'Edit campaign' : 'New campaign'}
      />

      <SectionCard title="Campaign name" icon={Mail}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Q3 funded-startup outreach"
        />
      </SectionCard>

      <SectionCard title="Sending mailbox" icon={Mail}>
        {mailboxes.length === 0 ? (
          <p className="text-sm text-slate-600">
            No active mailbox. Connect one first.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {mailboxes.map((m) => {
              const on = selectedMailboxes.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() =>
                    setSelectedMailboxes((prev) =>
                      on ? prev.filter((x) => x !== m.id) : [...prev, m.id],
                    )
                  }
                  className={clsx(
                    'rounded-xl border px-3.5 py-2 font-mono text-[13px] shadow-sm transition duration-200',
                    on
                      ? 'border-brand-600 bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/10'
                      : 'border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900',
                  )}
                >
                  {m.email}
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Draft with AI (Opus)"
        icon={Sparkles}
        bodyClassName="space-y-4"
      >
        {/* Required inputs. */}
        <Field label="Offer">
          <Input
            placeholder="What you're selling"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
          />
        </Field>
        <Field label="Ideal customer (ICP)">
          <Input
            placeholder="Who you're writing to"
            value={icp}
            onChange={(e) => setIcp(e.target.value)}
          />
        </Field>

        {/* Optional inputs that sharpen the copy. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={
              <>
                Sender name{' '}
                <span className="font-normal text-slate-400">(optional)</span>
              </>
            }
          >
            <Input
              placeholder="Who the emails are from"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />
          </Field>
          <Field
            label={
              <>
                Sender company{' '}
                <span className="font-normal text-slate-400">(optional)</span>
              </>
            }
          >
            <Input
              placeholder="Your company"
              value={senderCompany}
              onChange={(e) => setSenderCompany(e.target.value)}
            />
          </Field>
        </div>
        <Field
          label={
            <>
              Prospect pain points{' '}
              <span className="font-normal text-slate-400">(optional)</span>
            </>
          }
        >
          <Textarea
            placeholder="The problems your reader feels"
            value={painPoints}
            onChange={(e) => setPainPoints(e.target.value)}
            rows={2}
          />
        </Field>
        <Field
          label={
            <>
              Value props / proof points{' '}
              <span className="font-normal text-slate-400">(optional)</span>
            </>
          }
        >
          <Textarea
            placeholder="Why you, with specifics"
            value={valueProps}
            onChange={(e) => setValueProps(e.target.value)}
            rows={2}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={
              <>
                Call to action{' '}
                <span className="font-normal text-slate-400">(optional)</span>
              </>
            }
          >
            <Input
              placeholder="e.g. book a 15 min call"
              value={cta}
              onChange={(e) => setCta(e.target.value)}
            />
          </Field>
          <Field label="Tone">
            <Input
              placeholder="direct, friendly"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
            />
          </Field>
        </div>

        {/* Sequence shape. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Number of emails">
            <Select
              value={stepCount}
              onChange={(e) => setStepCount(Number(e.target.value))}
            >
              {Array.from(
                { length: SEQUENCE_BOUNDS.stepCount.max },
                (_, i) => i + 1,
              ).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Wait days between emails">
            <Select
              value={waitDays}
              onChange={(e) => setWaitDays(Number(e.target.value))}
            >
              {Array.from(
                {
                  length:
                    SEQUENCE_BOUNDS.waitDays.max -
                    SEQUENCE_BOUNDS.waitDays.min +
                    1,
                },
                (_, i) => i + SEQUENCE_BOUNDS.waitDays.min,
              ).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Button
          variant="ghost"
          icon={Sparkles}
          onClick={() => void draft()}
          disabled={drafting}
        >
          {drafting
            ? 'Drafting…'
            : hasDraftedSteps
              ? 'Regenerate'
              : 'Draft with AI'}
        </Button>
      </SectionCard>

      <SectionCard
        title="Campaign collateral"
        icon={Globe}
        bodyClassName="space-y-4"
      >
        {!isEdit ? (
          <p className="text-sm text-slate-600">
            Save the campaign first, then reopen it to turn your product website
            into a landing page and grounded copy.
          </p>
        ) : (
          <>
            <Field
              label="Product website URL"
              hint="RevBase scrapes this to harvest your real images and extract a brand kit, landing page, and reply assets. Nothing image-heavy ever touches the email body."
            >
              <div className="flex flex-wrap gap-2">
                <Input
                  className="min-w-[240px] flex-1"
                  placeholder="https://yourproduct.com"
                  value={productUrl}
                  onChange={(e) => setProductUrl(e.target.value)}
                />
                <Button
                  variant="ghost"
                  icon={Globe}
                  onClick={() => void generateCollateral()}
                  disabled={collateralBusy}
                >
                  {collateralBusy ? 'Working…' : 'Generate collateral'}
                </Button>
              </div>
            </Field>

            {collateral?.status === 'scraping' && (
              <CollateralStatusLine label="Scraping the site and harvesting images…" />
            )}
            {collateral?.status === 'extracting' && (
              <CollateralStatusLine label="Extracting brand kit and landing copy…" />
            )}

            {collateral?.status === 'failed' && (
              <div
                role="alert"
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <span className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                  {collateral.error || 'Could not build collateral.'}
                </span>
                <Button
                  variant="ghost"
                  icon={RefreshCw}
                  onClick={() => void regenerateCollateral()}
                  disabled={collateralBusy}
                >
                  Try again
                </Button>
              </div>
            )}

            {(collateral?.status === 'ready' ||
              collateral?.status === 'published') && (
              <div className="space-y-5">
                {/* Brand kit preview */}
                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="min-w-0">
                    <p className="font-display text-sm font-semibold text-slate-900">
                      {collateral.brandKit?.name || collateral.domain}
                    </p>
                    {collateral.brandKit?.tagline && (
                      <p className="mt-0.5 text-sm text-slate-600">
                        {collateral.brandKit.tagline}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[
                      collateral.brandKit?.colors?.primary,
                      collateral.brandKit?.colors?.accent,
                      collateral.brandKit?.colors?.bg,
                    ]
                      .filter((c): c is string => Boolean(c))
                      .map((c) => (
                        <span
                          key={c}
                          title={c}
                          className="h-6 w-6 rounded-md border border-slate-300"
                          style={{ backgroundColor: c }}
                        />
                      ))}
                  </div>
                </div>

                {/* Value props (read-only preview; edit via Regenerate in MVP) */}
                {collateral.valueProps.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Value props
                    </p>
                    <ul className="space-y-1.5">
                      {collateral.valueProps.map((v, i) => (
                        <li
                          key={i}
                          className="flex gap-2 text-sm text-slate-700"
                        >
                          <CheckCircle2
                            className="mt-0.5 h-4 w-4 shrink-0 text-brand-600"
                            strokeWidth={2}
                            aria-hidden
                          />
                          {v}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Harvested image grid: select / deselect for the landing page */}
                {collateral.images.length > 0 && (
                  <div>
                    <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <ImageIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                      Harvested images ({collateral.images.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {collateral.images.map((img) => {
                        const on = imgSelected[img.imageId] ?? false;
                        return (
                          <button
                            key={img.imageId}
                            type="button"
                            onClick={() =>
                              setImgSelected((prev) => ({
                                ...prev,
                                [img.imageId]: !on,
                              }))
                            }
                            className={clsx(
                              'group relative aspect-video overflow-hidden rounded-lg border transition',
                              on
                                ? 'border-brand-600 ring-2 ring-brand-500/30'
                                : 'border-slate-200 opacity-70 hover:opacity-100',
                            )}
                            title={img.alt || img.role || img.imageId}
                          >
                            {img.previewUrl ? (
                              <img
                                src={img.previewUrl}
                                alt={img.alt || ''}
                                loading="lazy"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="flex h-full w-full items-center justify-center bg-slate-100 text-[10px] text-slate-400">
                                {img.role || 'image'}
                              </span>
                            )}
                            {on && (
                              <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 text-white">
                                <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Publish + landing link */}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    icon={Send}
                    onClick={() => void publishLanding()}
                    disabled={publishing}
                  >
                    {publishing
                      ? 'Publishing…'
                      : collateral.status === 'published'
                        ? 'Republish landing page'
                        : 'Publish landing page'}
                  </Button>
                  <Button
                    variant="ghost"
                    icon={RefreshCw}
                    onClick={() => void regenerateCollateral()}
                    disabled={collateralBusy}
                  >
                    Regenerate
                  </Button>
                </div>

                {landingUrl && (
                  <div className="space-y-2 rounded-xl border border-brand-100 bg-brand-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                      Landing page is live
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-lg border border-brand-200 bg-white px-3 py-2 font-mono text-[13px] text-slate-700">
                        {landingUrl}
                      </code>
                      <Button
                        variant="ghost"
                        icon={Copy}
                        onClick={copyLandingUrl}
                        className="px-3 py-2 text-xs"
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                      <a
                        href={landingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="ghost"
                          icon={ExternalLink}
                          className="px-3 py-2 text-xs"
                        >
                          View
                        </Button>
                      </a>
                    </div>
                    <Button
                      variant="ghost"
                      icon={Plus}
                      onClick={insertLandingLink}
                      className="px-3 py-2 text-xs"
                    >
                      Insert {'{{landingUrl}}'} link into email steps
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </SectionCard>

      <section className="space-y-3">
        <CardTitle icon={Send} className="text-lg">
          Sequence
        </CardTitle>
        <p className="text-xs text-slate-500">
          Merge variables:{' '}
          {[
            '{{firstName}}',
            '{{lastName}}',
            '{{company}}',
            '{{title}}',
            '{{email}}',
            '{{domain}}',
            '{{phishfenceReport}}',
            '{{dmarcStatus}}',
            '{{dmarcPolicy}}',
            '{{spfStatus}}',
            '{{securityGrade}}',
            '{{landingUrl}}',
          ].map((v, i, arr) => (
            <span key={v}>
              <code className="font-mono text-slate-700">{v}</code>
              {i < arr.length - 1 ? ', ' : ''}
            </span>
          ))}
          . The enrichment tokens render real DMARC and SPF facts when a contact
          is enriched, and a neutral phrase otherwise, so a template never sends
          a false claim. {'{{landingUrl}}'} resolves to your tracked landing page
          at send, or an empty string until you publish one.
        </p>
        {steps.map((step, idx) => (
          <Card key={idx} className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-slate-500">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 font-semibold text-brand-700 ring-1 ring-inset ring-brand-600/10">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                {step.type === 'email' ? (
                  <Mail className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                ) : (
                  <Clock className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                )}
                {step.type}
              </span>
              <button
                className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400 transition-colors hover:text-red-600"
                onClick={() => removeStep(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                remove
              </button>
            </div>
            {step.type === 'email' ? (
              <>
                <Input
                  placeholder="Subject: use {{firstName}}, {{company}}"
                  value={step.subjectTemplate}
                  onChange={(e) =>
                    updateStep(idx, { subjectTemplate: e.target.value })
                  }
                />
                <Textarea
                  placeholder="Body: {{firstName}}, {{company}}, {{title}}"
                  value={step.bodyTemplate}
                  onChange={(e) =>
                    updateStep(idx, { bodyTemplate: e.target.value })
                  }
                  rows={5}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Instruction (optional): make it shorter, stronger opener…"
                    value={rewriteInstructions[idx] ?? ''}
                    onChange={(e) =>
                      setRewriteInstructions((prev) => ({
                        ...prev,
                        [idx]: e.target.value,
                      }))
                    }
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    icon={Wand2}
                    onClick={() => void rewrite(idx)}
                    disabled={rewritingIdx !== null}
                  >
                    {rewritingIdx === idx ? 'Improving…' : 'Improve with AI'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                Wait
                <Input
                  type="number"
                  min={1}
                  value={step.waitDays}
                  onChange={(e) =>
                    updateStep(idx, { waitDays: Number(e.target.value) })
                  }
                  className="w-20"
                />
                days
              </div>
            )}
          </Card>
        ))}
        <div className="flex gap-2">
          <Button variant="ghost" icon={Plus} onClick={addEmail}>
            Email step
          </Button>
          <Button variant="ghost" icon={Clock} onClick={addWait}>
            Wait step
          </Button>
        </div>
      </section>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          icon={Save}
          onClick={() => void save()}
          disabled={saving || deleting}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save campaign'}
        </Button>
        {isEdit && (
          <Button
            variant="destructive"
            icon={Trash2}
            onClick={() => void remove()}
            disabled={saving || deleting}
          >
            {deleting ? 'Deleting…' : 'Delete campaign'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** A live collateral status row: a shimmer dot plus the current step label. */
function CollateralStatusLine({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <Skeleton className="h-4 w-4 rounded-full" />
      <span className="text-sm text-slate-600">{label}</span>
    </div>
  );
}
